class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.modelWords = [
      'ultra',
      'watch',
      'plus',
      'fold',
      'flip',
      'mini',
      'note',
      'edge',
      'lite',
      'buds',
      'book',
      'max',
      'pro',
      'tab',
      'pad',
      'air',
      'fe',
      'se',
    ];
    this.seriesLetters = ['s', 'r', 'a', 'm', 'x', 'z', 'c', 'g', 'p', 'f', 'e', 'n', 't', 'y'];
    this.safeOneLetterModelPrefixes = ['s', 'a', 'm', 'x', 'z'];
    this.modelFamilyWords = [
      'pixel',
      'iphone',
      'ipad',
      'galaxy',
      'redmi',
      'poco',
      'mate',
      'nova',
      'xperia',
      'oneplus',
      'motorola',
      'huawei',
      'honor',
      'realme',
      'oppo',
      'vivo',
    ];

    this.searchSynonyms = {
      'pixel': 'google pixel',
      'galaxy': 'samsung galaxy',
    };

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !this.getCompactValue(newSearchTerm).startsWith(this.getCompactValue(this.searchTerm))) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    if (!previousTerm) return;

    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;

    if (currentButtonText) {
      const escapedPreviousTerm = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const matches = currentButtonText.match(new RegExp(escapedPreviousTerm, 'g'));

      if (matches?.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }

      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i < allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = this.getCacheKey(searchTerm);
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();

    const currentSearchTerm = searchTerm;
    const searchQueries = this.getSearchQueries(searchTerm);
if (searchTerm.toLowerCase().includes('fold 8')) searchQueries.push(`${searchTerm} ultra`);

    Promise.all(
      searchQueries.map((query, index) =>
        this.fetchSearchResults(query, this.abortController.signal)
          .then((resultsMarkup) => ({ query, index, resultsMarkup }))
          .catch((error) => {
            if (this.isAbortError(error)) return null;
            return { query, index, error };
          })
      )
    )
      .then((responses) => {
        if (currentSearchTerm !== this.searchTerm) return;

        const successfulResponses = responses.filter((response) => response?.resultsMarkup);

        if (!successfulResponses.length) {
          this.close();
          const firstError = responses.find((response) => response?.error)?.error;
          if (firstError) throw firstError;
          return;
        }

        const baseDoc = new DOMParser().parseFromString(successfulResponses[0].resultsMarkup, 'text/html');
const seen = new Set(Array.from(baseDoc.querySelectorAll('li a')).map(a => a.href.split('?')[0]));

successfulResponses.slice(1).forEach(res => {
  const doc = new DOMParser().parseFromString(res.resultsMarkup, 'text/html');
  doc.querySelectorAll('ul').forEach((ul, i) => {
    const baseUl = baseDoc.querySelectorAll('ul')[i];
    if (baseUl) ul.querySelectorAll('li').forEach(li => {
      const link = li.querySelector('a')?.href?.split('?')[0];
      if (link && !seen.has(link)) { 
        seen.add(link); 
        baseUl.appendChild(li); 
      }
    });
  });
});

const resultsMarkup = this.rankSearchResults(baseDoc.body.innerHTML, currentSearchTerm);

        // Save bandwidth keeping the cache in all instances synced.
        // Cache the final result for all equivalent query variants as well.
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          this.getSearchQueries(currentSearchTerm).forEach((query) => {
            predictiveSearchInstance.cachedResults[this.getCacheKey(query)] = resultsMarkup;
          });
        });

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (this.isAbortError(error)) return;
        this.close();
        throw error;
      });
  }

  fetchSearchResults(searchQuery, signal) {
    return fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchQuery)}&section_id=predictive-search`, {
      signal,
    })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const parsedHTML = new DOMParser().parseFromString(text, 'text/html');
        return parsedHTML.querySelector('#shopify-section-predictive-search')?.innerHTML || '';
      });
  }

  getSearchQueries(searchTerm) {
    let cleanSearchTerm = searchTerm.trim().replace(/\s+/g, ' ');

    // Query Rewrite automat via dicționar de sinonime
    for (const [key, value] of Object.entries(this.searchSynonyms)) {
      const regex = new RegExp(`(^|\\s)${key}(\\s|\\d|$)`, 'i');
      if (regex.test(cleanSearchTerm)) {
        cleanSearchTerm = cleanSearchTerm.replace(new RegExp(`(^|\\s)${key}`, 'i'), `$1${value}`);
        break;
      }
    }

    const normalizedTerm = this.normalizeSearchText(cleanSearchTerm);
    const searchQueries = [];
    const usedQueries = new Set();

    const addQuery = (query) => {
      const cleanQuery = query?.trim().replace(/\s+/g, ' ');
      if (!cleanQuery) return;

      const queryKey = cleanQuery.toLowerCase();
      if (usedQueries.has(queryKey)) return;

      usedQueries.add(queryKey);
      searchQueries.push(cleanQuery);
    };

    addQuery(cleanSearchTerm);

    if (!normalizedTerm || normalizedTerm.length < 2) return searchQueries;

    addQuery(normalizedTerm);

    if (!this.isLikelyModelQuery(normalizedTerm)) return searchQueries;

    const boundarySeparated = normalizedTerm
      .replace(/(\d)([a-z])/g, '$1 $2')
      .replace(/([a-z])(\d)/g, '$1 $2')
      .trim()
      .replace(/\s+/g, ' ');
    const compactTerm = this.getCompactValue(normalizedTerm);
    const smartSplitTerm = this.splitKnownModelWords(boundarySeparated || normalizedTerm);
    const joinedSmartSplitTerm = this.joinSingleLetterNumberTokens(smartSplitTerm);
    const relaxedSmartSplitTerm = this.removeLeadingOneLetterModelPrefix(smartSplitTerm);

    addQuery(boundarySeparated);
    addQuery(smartSplitTerm);
    addQuery(joinedSmartSplitTerm);
    addQuery(relaxedSmartSplitTerm);
    addQuery(compactTerm);
    addQuery(this.getCompactValue(smartSplitTerm));
    addQuery(this.getCompactValue(joinedSmartSplitTerm));
    addQuery(this.getCompactValue(relaxedSmartSplitTerm));

    this.getSeriesNumberQueries(smartSplitTerm || normalizedTerm).forEach(addQuery);

    return searchQueries.slice(0, 10);
  }

  getBestSearchResponse(responses, searchTerm) {
    const scoredResponses = responses.map((response) => {
      const markupScore = this.getMarkupScore(response.resultsMarkup, searchTerm);

      return {
        ...response,
        score: markupScore.bestScore + Math.min(markupScore.resultCount, 10) + (response.index === 0 ? 0.25 : 0),
      };
    });

    return scoredResponses.sort((a, b) => b.score - a.score || a.index - b.index)[0];
  }

  getMarkupScore(resultsMarkup, searchTerm) {
    const parsedHTML = this.parseResultsMarkup(resultsMarkup);
    const resultItems = Array.from(parsedHTML.querySelectorAll('li')).filter((item) => item.textContent.trim());
    const scores = resultItems.map((item) => this.scoreResultText(item.textContent, searchTerm));

    return {
      bestScore: scores.length ? Math.max(...scores) : 0,
      resultCount: resultItems.length,
    };
  }

  rankSearchResults(resultsMarkup, searchTerm) {
    const parsedHTML = this.parseResultsMarkup(resultsMarkup);
    const container = parsedHTML.body.firstElementChild;

    if (!container) return resultsMarkup;

    const shouldNarrowResults = this.shouldNarrowResults(searchTerm);
    const strongResultThreshold = this.getStrongResultThreshold(searchTerm);

    container.querySelectorAll('ul').forEach((list) => {
      const resultItems = Array.from(list.children).filter((child) => child.matches('li'));
      if (resultItems.length < 2) return;

      const scoredItems = resultItems.map((item, index) => ({
        item,
        index,
        score: this.scoreResultText(item.textContent, searchTerm),
      }));

      if (!scoredItems.some((scoredItem) => scoredItem.score > 0)) return;

      let itemsToRender = scoredItems.sort((a, b) => b.score - a.score || a.index - b.index);

      if (shouldNarrowResults) {
        const strongItems = itemsToRender.filter((scoredItem) => scoredItem.score >= strongResultThreshold);

        // Narrow only when we have at least one strong match. If Shopify returns only weak matches,
        // keep them visible instead of showing an empty dropdown.
        if (strongItems.length) itemsToRender = strongItems;
      }

      resultItems.forEach((item) => item.remove());
      itemsToRender.forEach((scoredItem) => list.appendChild(scoredItem.item));
    });

    return container.innerHTML;
  }

  scoreResultText(text, searchTerm) {
    const normalizedText = this.normalizeSearchText(text);
    const normalizedSearchTerm = this.normalizeSearchText(searchTerm);
    const splitSearchTerm = this.splitKnownModelWords(normalizedSearchTerm);
    const searchTokens = splitSearchTerm.split(' ').filter(Boolean);
    const relaxedSearchTokens = this.removeLeadingOneLetterModelPrefix(splitSearchTerm).split(' ').filter(Boolean);
    const textTokens = this.splitKnownModelWords(normalizedText).split(' ').filter(Boolean);
    const compactText = this.getCompactValue(normalizedText);
    const compactSearchTerm = this.getCompactValue(normalizedSearchTerm);
    const compactSplitSearchTerm = this.getCompactValue(splitSearchTerm);

    if (!normalizedText || !searchTokens.length) return 0;

    let score = 0;

    // Strongest signal: exact model match, independent of spaces or hyphens.
    // Example: zfold4 = z fold 4, iphone6 = iphone 6, a50 = a 50.
    if (this.containsCompactModel(compactText, compactSearchTerm)) score += 1000;
    if (
      compactSplitSearchTerm &&
      compactSplitSearchTerm !== compactSearchTerm &&
      this.containsCompactModel(compactText, compactSplitSearchTerm)
    ) {
      score += 950;
    }

    // Strong signal: exact consecutive token match.
    if (this.hasConsecutiveTokens(textTokens, searchTokens)) score += 900;

    // Useful fallback for products where a one-letter family prefix is missing from the title.
    // Example: customer searches zfold4, title may be written as Fold 4.
    if (
      relaxedSearchTokens.length &&
      relaxedSearchTokens.length !== searchTokens.length &&
      this.hasConsecutiveTokens(textTokens, relaxedSearchTokens)
    ) {
      score += 800;
    }

    // General brand + number match.
    // Examples: "samsung 26" should match "Samsung Galaxy S26";
    // "canon 50" should match "Canon EOS R50".
    if (this.hasBrandNumberMatch(textTokens, searchTokens)) score += 760;

    // Weaker signal: all terms appear in the same order, but not necessarily together.
    // This keeps valid secondary results, but below exact model matches.
    if (this.hasOrderedTokens(textTokens, searchTokens)) score += 300;

    score += searchTokens.filter((token) => textTokens.includes(token)).length * 10;

    return score;
  }

  containsCompactModel(compactText, compactSearchTerm) {
    if (!compactText || !compactSearchTerm) return false;

    let matchIndex = compactText.indexOf(compactSearchTerm);

    while (matchIndex !== -1) {
      const nextCharacter = compactText.charAt(matchIndex + compactSearchTerm.length);

      // Avoid treating a shorter numeric model as a match for a longer one.
      // Example: "a5" should not be a strong compact match for "a50".
      if (/\d$/.test(compactSearchTerm) && /^\d$/.test(nextCharacter)) {
        matchIndex = compactText.indexOf(compactSearchTerm, matchIndex + 1);
        continue;
      }

      return true;
    }

    return false;
  }

  hasBrandNumberMatch(textTokens, searchTokens) {
    const numberTokens = searchTokens.filter((token) => /^\d+$/.test(token));
    const firstNumberIndex = searchTokens.findIndex((token) => /^\d+$/.test(token));

    if (!numberTokens.length || firstNumberIndex <= 0) return false;

    const brandLikeTokens = searchTokens
      .slice(0, firstNumberIndex)
      .filter((token) => /^[a-z]+$/.test(token) && token.length >= 3 && !this.modelWords.includes(token));

    if (!brandLikeTokens.length) return false;

    return brandLikeTokens.every((token) => textTokens.includes(token)) &&
      numberTokens.every((token) => this.hasModelNumberToken(textTokens, token));
  }

  hasModelNumberToken(textTokens, numberToken) {
    return textTokens.some((token, index) => {
      if (token !== numberToken) return false;

      const previousToken = textTokens[index - 1] || '';
      const nextToken = textTokens[index + 1] || '';

      return /^[a-z]$/.test(previousToken) ||
        /^[a-z]$/.test(nextToken) ||
        this.modelWords.includes(previousToken) ||
        this.modelWords.includes(nextToken) ||
        this.modelFamilyWords.includes(previousToken) ||
        this.modelFamilyWords.includes(nextToken);
    });
  }

  getSeriesNumberQueries(value) {
    const tokens = this.splitKnownModelWords(value).split(' ').filter(Boolean);
    const firstNumberIndex = tokens.findIndex((token) => /^\d+$/.test(token));

    if (firstNumberIndex <= 0) return [];

    const numberToken = tokens[firstNumberIndex];

    // Keep this fallback focused. One-digit models like iphone 6 and z fold 4 are already handled
    // by normal spacing/compact matching and should not trigger extra series-prefix searches.
    if (numberToken.length < 2) return [];

    const brandLikeTokens = tokens
      .slice(0, firstNumberIndex)
      .filter((token) => /^[a-z]+$/.test(token) && token.length >= 3 && !this.modelWords.includes(token));

    if (!brandLikeTokens.length) return [];

    const brandQuery = brandLikeTokens.join(' ');
    const primaryBrandToken = brandLikeTokens[0];
    const prefixLetters = this.getOrderedSeriesLetters(primaryBrandToken).slice(0, 4);
    const queries = [];

    prefixLetters.forEach((letter) => {
      queries.push(`${brandQuery} ${letter}${numberToken}`);
      queries.push(`${letter}${numberToken}`);
    });

    return queries;
  }

  getOrderedSeriesLetters(primaryBrandToken) {
    const orderedLetters = [];
    const primaryLetter = primaryBrandToken?.charAt(0);

    if (primaryLetter) orderedLetters.push(primaryLetter);

    this.seriesLetters.forEach((letter) => {
      if (!orderedLetters.includes(letter)) orderedLetters.push(letter);
    });

    return orderedLetters;
  }

  shouldNarrowResults(searchTerm) {
    const searchTokens = this.splitKnownModelWords(searchTerm).split(' ').filter(Boolean);

    return searchTokens.length >= 2 &&
      searchTokens.some((token) => /^\d+$/.test(token)) &&
      searchTokens.some((token) => /^[a-z]+$/.test(token));
  }

  getStrongResultThreshold() {
    return 700;
  }

  normalizeSearchText(value) {
    return value
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  getCompactValue(value) {
    return this.normalizeSearchText(value).replace(/\s+/g, '');
  }

  isLikelyModelQuery(value) {
    const normalizedValue = this.normalizeSearchText(value);
    if (normalizedValue.length < 2) return false;
    if (/\d/.test(normalizedValue)) return true;

    return normalizedValue.split(' ').some((token) => this.splitAlphaModelToken(token).length > 1);
  }

  splitKnownModelWords(value) {
    const normalizedValue = this.normalizeSearchText(value)
      .replace(/(\d)([a-z])/g, '$1 $2')
      .replace(/([a-z])(\d)/g, '$1 $2');

    const tokens = normalizedValue.split(' ');
    const outputTokens = [];

    tokens.forEach((token, index) => {
      let splitTokens = this.splitAlphaModelToken(token);

      outputTokens.push(...splitTokens);
    });

    return outputTokens.join(' ').trim().replace(/\s+/g, ' ');
  }

  joinSingleLetterNumberTokens(value) {
    const tokens = this.normalizeSearchText(value).split(' ').filter(Boolean);
    const outputTokens = [];

    for (let i = 0; i < tokens.length; i++) {
      if (tokens[i].length === 1 && /^\d+$/.test(tokens[i + 1] || '')) {
        outputTokens.push(`${tokens[i]}${tokens[i + 1]}`);
        i++;
      } else {
        outputTokens.push(tokens[i]);
      }
    }

    return outputTokens.join(' ');
  }

  splitAlphaModelToken(token) {
    if (!/^[a-z]+$/.test(token) || token.length <= 2) return [token];

    const parts = [];
    let remainingToken = token;

    while (remainingToken.length > 2) {
      const matchingWord = this.modelWords.find((word) => {
        if (!remainingToken.endsWith(word) || remainingToken.length <= word.length) return false;

        const prefix = remainingToken.slice(0, -word.length);

        // Avoid risky splits like case -> ca se or chair -> ch air.
        // Allow one-letter prefixes for model families such as zfold / zflip / xpro.
        return prefix.length === 1 || prefix.length >= 3;
      });

      if (!matchingWord) break;

      parts.unshift(matchingWord);
      remainingToken = remainingToken.slice(0, -matchingWord.length);
    }

    if (remainingToken) parts.unshift(remainingToken);

    return parts.length ? parts : [token];
  }

  removeLeadingOneLetterModelPrefix(value) {
    const tokens = this.normalizeSearchText(value).split(' ').filter(Boolean);

    if (tokens.length >= 2 && tokens[0].length === 1 && this.modelWords.includes(tokens[1]) && tokens[1].length >= 4) {
      return tokens.slice(1).join(' ');
    }

    return tokens.join(' ');
  }

  hasConsecutiveTokens(textTokens, searchTokens) {
    if (!textTokens.length || !searchTokens.length || searchTokens.length > textTokens.length) return false;

    for (let i = 0; i <= textTokens.length - searchTokens.length; i++) {
      const isMatch = searchTokens.every((token, index) => textTokens[i + index] === token);
      if (isMatch) return true;
    }

    return false;
  }

  hasOrderedTokens(textTokens, searchTokens) {
    if (!textTokens.length || !searchTokens.length) return false;

    let currentIndex = 0;

    return searchTokens.every((token) => {
      const foundIndex = textTokens.indexOf(token, currentIndex);
      if (foundIndex === -1) return false;

      currentIndex = foundIndex + 1;
      return true;
    });
  }

  parseResultsMarkup(resultsMarkup) {
    return new DOMParser().parseFromString(`<div>${resultsMarkup}</div>`, 'text/html');
  }

  getCacheKey(searchTerm) {
    return this.normalizeSearchText(searchTerm).replace(/\s+/g, '-');
  }

  isAbortError(error) {
    return error?.name === 'AbortError' || error?.code === 20;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    if (this.statusElement) this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    const liveRegionCount = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegionCount) this.setLiveRegionText(liveRegionCount.textContent);
  }

  getResultsMaxHeight() {
  const header = document.querySelector('.section-header');
  const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
  this.resultsMaxHeight = `${Math.max(window.innerHeight - headerBottom, 0)}px`;
  return this.resultsMaxHeight;
}

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || this.getResultsMaxHeight();
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}